/**
 * saveIntegration — RPC contract for the one generic "save & verify"
 * integration callable.
 *
 * Cross-process contract between the admin Integrations panel's write seam
 * (`web/src/admin/settings/integrations/integrations.api.ts`) and
 * `firebase/src/settings/integrations.api.ts` (onCall handler).
 * The operator submits one registry id plus that id's declared secret
 * fields; the credential is proved by USING it, and only a proved
 * credential is stored. A verification failure is a normal `ok: false`
 * outcome — the handler also writes the status block it implies
 * (workspace.entity.ts's IntegrationStatus) and stores nothing; only
 * transport/auth failures throw, carrying the `details.reason` members
 * integration-save-failure.const.ts resolves. No credential value ever
 * travels back.
 *
 * Every declared secret field is `.nullish()`, not `.min(1)`-required —
 * REGISTRY (integrations.const.ts) itself stays untouched (secrets are
 * declared required THERE, the credential shape a fresh save proves), but
 * THIS contract additionally admits omitting a field on a re-save: the
 * generic handler resolves an omitted field against the already-stored
 * value before proving+persisting (integrations.api.ts's
 * `resolveDeclaredSecrets`), so rotating one field of a multi-field
 * credential (e.g. Apple Wallet's Pass Type ID certificate, independent of
 * its far-less-often-rotated WWDR intermediate) never forces re-supplying
 * every sibling field. A field omitted on a FIRST save — no credential
 * document for this id at all — still fails, `resolveDeclaredSecrets`
 * throwing `invalidInput`, so the first save of any id remains
 * whole-credential in practice. Once one is stored, an omitted field with
 * nothing at rest is one the registry declared after that save, and it
 * resolves absent rather than refusing; the provider is handed what exists
 * and answers by using it.
 *
 * Beyond `secrets`, a variant may declare its own SAVE-TIME OPERATIONAL
 * inputs — never a credential, so never REGISTRY's `secrets` shape: e.g.
 * Revolut's `activate`/`publicKey`/`currency`/`applePayDomain`, Lettermint's
 * `routeId`. These travel to that id's own `register` hook
 * (integrations.api.ts's `Verifiers.register`) alongside the resolved
 * secrets, for a provider-side effect the credential proof alone cannot
 * express — see each field's own `.describe()` for what it does.
 *
 * Outcome fields justified, evidence → signal → commitment:
 *   detail — the provider's own message, verbatim free text; empty when it
 *            said nothing — provider specificity lives here, so the reason
 *            vocabulary never grows a per-provider member.
 *   reason — the closed five-member verification vocabulary
 *            (IntegrationVerificationReasonSchema), `ok` on success.
 *   ok     — whether the credential proved itself by use.
 *   serviceAccountEmail — the resolved runtime identity a verify call named,
 *            when the provider is identity-based and has one to name (e.g.
 *            Google Wallet's own runtime service account, on either an
 *            authorization failure or a success) — null for every other id.
 *   certificateExpiresAt — the just-proved credential's own expiry,
 *            ISO-8601, when its material carries one (e.g. Apple Wallet's
 *            Pass Type ID certificate) — null for every other id.
 */
import { z } from "zod";
import { IntegrationVerificationReasonSchema } from "./integration-save-failure.const.ts";
import { REGISTRY } from "./integrations.const.ts";
import { Id } from "./workspace.entity.ts";

// Every REGISTRY-declared secret field, relaxed to nullish for the SAVE
// contract only — REGISTRY's own required z.ZodString stays the credential
// shape a fresh save must prove; omitting a field here signals "keep
// whatever is already stored" (this file's own header).
function nullishSecrets<T extends Record<string, z.ZodString>>(
	secrets: T,
): { [K in keyof T]: z.ZodOptional<z.ZodNullable<T[K]>> } {
	return Object.fromEntries(
		Object.entries(secrets).map(([field, schema]) => [field, schema.nullish()]),
	) as { [K in keyof T]: z.ZodOptional<z.ZodNullable<T[K]>> };
}

// Meta's own id vocabulary, transcribed from its references on 2026-08-30
// against Graph v26.0. `id` on the Application node
// (developers.facebook.com/docs/graph-api/reference/application/), `id` on
// the AdsPixel node (.../marketing-api/reference/ads-pixel/), and `page_id` /
// `instagram_user_id` on the ad-creative object story spec
// (.../marketing-api/reference/ad-creative-object-story-spec/) are each
// documented "numeric string" and nothing more. The AdAccount node's own id
// is documented separately as "the string act_{ad_account_id}"
// (.../marketing-api/reference/ad-account/), so it carries its own regex at
// its field rather than joining this one.
//
// No digit count appears here because Meta publishes none for any of these
// nodes. A length this repo invented would refuse an id Meta itself issued,
// and every surface repeating it would tell an operator that refusal was
// Meta's rule.
const META_NUMERIC_ID = z.string().regex(/^\d+$/);

// One variant per registry id, each variant's `secrets` the entry's own
// declared shape (fields nullish — see the file header), both levels
// `.strict()` so an undeclared field is rejected at the parse, never
// silently stripped into the stored doc: the parsed union IS the
// key-closure the stored shape's open record cannot enforce.
export const MetaAdsInputSchema = z
	.object({
		orgId: Id,
		id: z.literal("metaAds"),
		secrets: z.object(nullishSecrets(REGISTRY.metaAds.secrets)).strict(),
		pixelId: META_NUMERIC_ID.nullish().describe(
			"the Meta Pixel (dataset) id — public, numeric; register proves the token can read it and projects it onto the public workspace card's ads.meta.pixelId",
		),
		browserTag: z
			.boolean()
			.nullish()
			.describe(
				"whether the storefront also loads Meta's browser script — off unless the operator turns it on; flipping it on is what makes a visitor consent prompt exist, since only the script writes to the visitor's device. Nullish keeps the stored value",
			),
		// The advertiser context — which Meta app the credential pair belongs
		// to, which account the money is spent from, and which identity the ad
		// is published as. Each is a public identifier of a Meta object, so none
		// is a REGISTRY-declared secret; each is nullish, so a re-save keeps
		// whatever is already stored, exactly as a declared secret does.
		//
		// This is where each format is enforced, because this is where an
		// operator's own typing enters. The stored shape
		// (workspace.entity.ts's MetaAdvertiserSchema) deliberately keeps the
		// same fields loose: a format Meta later changes must refuse the next
		// save, never make an already-stored document unreadable. That
		// divergence in CONSTRAINT is the design; what must not diverge is
		// MEMBERSHIP, and this contract's own test pins the two key sets equal
		// in both directions rather than one spreading the other, so neither
		// end can inherit or lose a field without the other saying so.
		appId: META_NUMERIC_ID.nullish().describe(
			"the client's own Meta app — the app the access token was minted under and whose secret signs the proof each call carries",
		),
		adAccountId: z
			.string()
			.regex(/^act_\d+$/)
			.nullish()
			.describe(
				"the ad account campaigns are built in and billed to — the account whose currency names the unit every budget and spend figure counts in",
			),
		pageId: META_NUMERIC_ID.nullish().describe(
			"the Facebook page an ad is published as — the ad identity a viewer sees it run under",
		),
		instagramAccountId: META_NUMERIC_ID.nullish().describe(
			"the Instagram account an ad is published as, the one connected to the page",
		),
	})
	.strict();

export const SaveIntegrationInput = z.discriminatedUnion("id", [
	z
		.object({
			orgId: Id,
			id: z.literal("lettermint"),
			secrets: z.object(nullishSecrets(REGISTRY.lettermint.secrets)).strict(),
			routeId: z
				.string()
				.min(1)
				.nullish()
				.describe(
					"the Lettermint route id the delivery-status webhook registers on, from the route picker (listLettermintRoutes) — omitted leaves whatever route is already configured untouched, and with no route ever configured the webhook registration stays a no-op",
				),
		})
		.strict(),
	z
		.object({
			orgId: Id,
			id: z.literal("revolutSandbox"),
			confirmationCode: z
				.string()
				.regex(/^\d{6}$/)
				.optional()
				.describe(
					"the single-use confirmation code required server-side when activating sandbox from live",
				),
			secrets: z
				.object(nullishSecrets(REGISTRY.revolutSandbox.secrets))
				.strict(),
			activate: z
				.boolean()
				.nullish()
				.describe(
					"flips this environment to be the active one for new checkouts — gated server-side on this save's own secretKey having just proved itself and its webhook having just (re)registered, never on client-asserted state",
				),
			publicKey: z
				.string()
				.min(1)
				.nullish()
				.describe(
					"this environment's publishable key (pk_…) — projected onto the public workspace card's payment.publicKey only when this save also activates the environment",
				),
			currency: z
				.string()
				.min(1)
				.nullish()
				.describe(
					"the ISO-4217 currency new checkouts price and Revolut charges in for this workspace — one currency governs both environments",
				),
		})
		.strict(),
	z
		.object({
			orgId: Id,
			id: z.literal("revolutLive"),
			secrets: z.object(nullishSecrets(REGISTRY.revolutLive.secrets)).strict(),
			activate: z
				.boolean()
				.nullish()
				.describe(
					"flips this environment to be the active one for new checkouts — gated server-side on this save's own secretKey having just proved itself and its webhook having just (re)registered, never on client-asserted state",
				),
			publicKey: z
				.string()
				.min(1)
				.nullish()
				.describe(
					"this environment's publishable key (pk_…) — projected onto the public workspace card's payment.publicKey only when this save also activates the environment",
				),
			currency: z
				.string()
				.min(1)
				.nullish()
				.describe(
					"the ISO-4217 currency new checkouts price and Revolut charges in for this workspace — one currency governs both environments",
				),
			applePayDomain: z
				.string()
				.min(1)
				.nullish()
				.describe(
					"the domain to register for Apple Pay on the live environment — live-only; Apple Pay never applies to sandbox",
				),
		})
		.strict(),
	z
		.object({
			orgId: Id,
			id: z.literal("appleWallet"),
			secrets: z.object(nullishSecrets(REGISTRY.appleWallet.secrets)).strict(),
		})
		.strict(),
	z
		.object({
			orgId: Id,
			id: z.literal("googleWallet"),
			secrets: z.object(nullishSecrets(REGISTRY.googleWallet.secrets)).strict(),
		})
		.strict(),
	z
		.object({
			orgId: Id,
			id: z.literal("gemini"),
			secrets: z.object(nullishSecrets(REGISTRY.gemini.secrets)).strict(),
		})
		.strict(),
	MetaAdsInputSchema,
]);
export type SaveIntegrationInput = z.infer<typeof SaveIntegrationInput>;

export const SaveIntegrationOutcome = z.object({
	detail: z.string(),
	reason: IntegrationVerificationReasonSchema,
	ok: z.boolean(),
	serviceAccountEmail: z
		.string()
		.nullish()
		.describe(
			"the resolved runtime identity a verify call named, when the provider is identity-based and has one to name — null for every other id",
		),
	certificateExpiresAt: z
		.string()
		.nullish()
		.describe(
			"the just-proved credential's own expiry, ISO-8601, when its material carries one — null for every other id",
		),
});
export type SaveIntegrationOutcome = z.infer<typeof SaveIntegrationOutcome>;
